import Debug from 'debug';
import jsonwebtoken from 'jsonwebtoken';

import { Serializable } from '../lib/Serializable';
import { userService } from '../services/UserService';

export type TokenDetails = {
  expires_in: string;
  access_token: string;
};

export type SerializedTokenDetails = {
  accessToken: string;
  expiry: number;
};

/**
 * Model a single access token.
 */
export class Token implements Serializable<SerializedTokenDetails> {
  debug = Debug('o365:Token');
  protected readonly data: TokenDetails;
  expiry: number;

  constructor(details: TokenDetails) {
    this.data = details;
    this.expiry = Date.now() + parseInt(details.expires_in, 10);
  }

  getAccessToken() {
    if (Date.now() > this.expiry) {
      this.debug('token expired, triggering logout');
      userService.logout({ triggered: false });
      return null;
    }
    return this.data.access_token;
  }

  isExpired(): boolean | null {
    const expiration = getExpirationFromToken(this.data.access_token);
    this.debug('isExpired() expiration', expiration);
    if (expiration) {
      return !!(expiration.getTime() < Date.now());
    }
    return null;
  }

  serialize(): SerializedTokenDetails {
    return {
      accessToken: this.data.access_token,
      expiry: this.expiry,
    };
  }
}

/**
 * Decode the JWT and grab the expiration date.
 */
export function getExpirationFromToken(token: string) {
  // figure out the expiration time from the JWT
  try {
    const decodedToken = jsonwebtoken.decode(token);
    if (decodedToken === null) {
      return false;
    }
    const expiration = new Date(0);
    expiration.setUTCSeconds(parseInt((decodedToken as Record<string, string>).exp, 10));
    return expiration;
  } catch (err) {
    console.error('Error decoding the JWT', err);
    return false;
  }
}

/**
 * Create a new Token model from a JWT.
 */
export function createTokenFromJWT(jwt: string) {
  const expiration = getExpirationFromToken(jwt);
  if (!expiration) {
    return null;
  }

  return createTokenFromSerializedToken({ accessToken: jwt, expiry: expiration.getTime() });
}

/**
 * Create a new Token model from the serialized token data structure.
 */
export function createTokenFromSerializedToken(token: SerializedTokenDetails | null) {
  if (!token) {
    return null;
  }
  return new Token({
    access_token: token.accessToken,
    expires_in: `${Math.abs(token.expiry - Date.now())}`,
  });
}
